<script setup>
import {ref, watch} from 'vue';
import draggable from 'vuedraggable';

const props = defineProps({
    chapters: {
        type: Array,
        required: true,
    },
    chapterGroups: {
        type: Array,
        default: () => [],
    },
    courseId: {
        type: Number,
        required: true,
    },
});

const emit = defineEmits(['edit-settings', 'delete:chapter', 'changed']);

const drag = ref(false);
const groupedChapters = ref([]);
const ungroupedChapters = ref([]);
const savingLayout = ref(false);
const newGroupTitle = ref('');
const creatingGroup = ref(false);
const editingGroupId = ref(null);
const showCreateGroupForm = ref(false);

const clone = (value) => JSON.parse(JSON.stringify(value));

const hydrateLayoutFromProps = () => {
    // Skip hydration if currently saving to prevent race conditions
    if (savingLayout.value) {
        return;
    }

    const groups = clone(props.chapterGroups)
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((group) => ({
            ...group,
            chapters: [],
        }));

    const groupsById = new Map(groups.map((group) => [group.id, group]));

    const sortedChapters = clone(props.chapters)
        .sort((a, b) => a.sort_order - b.sort_order);

    const ungrouped = [];
    sortedChapters.forEach((chapter) => {
        if (chapter.course_chapter_group_id && groupsById.has(chapter.course_chapter_group_id)) {
            groupsById.get(chapter.course_chapter_group_id).chapters.push(chapter);

            return;
        }

        chapter.course_chapter_group_id = null;
        ungrouped.push(chapter);
    });

    groupedChapters.value = groups;
    ungroupedChapters.value = ungrouped;
};

watch(() => props.chapters, hydrateLayoutFromProps, {immediate: true, deep: true});
watch(() => props.chapterGroups, hydrateLayoutFromProps, {immediate: true, deep: true});

const deleteChapter = async (chapter) => {
    const res = await axios.delete(route('api.course.backoffice.chapter.destroy', {
        course_id: chapter.course_id,
        chapter_id: chapter.id,
    }));

    if (res.data.success) {
        emit('delete:chapter', chapter);
    }
};

const createGroup = async () => {
    if (creatingGroup.value || newGroupTitle.value.trim().length === 0) {
        return;
    }

    creatingGroup.value = true;
    try {
        const res = await axios.post(route('api.course.backoffice.chapter-group.add', {
            course_id: props.courseId,
        }), {
            title: newGroupTitle.value,
        });

        if (res.data.success) {
            newGroupTitle.value = '';
            showCreateGroupForm.value = false;
            emit('changed');
        }
    } finally {
        creatingGroup.value = false;
    }
};

const saveGroup = async (group) => {
    if (!group.title || String(group.title).trim().length === 0) {
        return;
    }

    await axios.put(route('api.course.backoffice.chapter-group.update', {
        course_id: props.courseId,
        chapter_group_id: group.id,
    }), {
        title: group.title,
    });

    emit('changed');
};

const deleteGroup = async (group) => {
    const res = await axios.delete(route('api.course.backoffice.chapter-group.destroy', {
        course_id: props.courseId,
        chapter_group_id: group.id,
    }));

    if (res.data.success) {
        emit('changed');
    }
};

const toggleGroupEdit = (groupId) => {
    editingGroupId.value = editingGroupId.value === groupId
        ? null
        : groupId;
};

const toggleCreateGroupForm = () => {
    showCreateGroupForm.value = !showCreateGroupForm.value;

    if (!showCreateGroupForm.value) {
        newGroupTitle.value = '';
    }
};

const persistChapterLayout = async () => {
    const chapterPayload = {};
    let sortOrder = 1;

    ungroupedChapters.value.forEach((chapter) => {
        chapter.course_chapter_group_id = null;
        chapterPayload[chapter.id] = {
            sort_order: sortOrder,
            course_chapter_group_id: null,
        };
        sortOrder += 1;
    });

    groupedChapters.value.forEach((group) => {
        group.chapters.forEach((chapter) => {
            chapter.course_chapter_group_id = group.id;
            chapterPayload[chapter.id] = {
                sort_order: sortOrder,
                course_chapter_group_id: group.id,
            };
            sortOrder += 1;
        });
    });

    await axios.put(route('api.course.backoffice.chapter.order', props.courseId), {
        chapters: chapterPayload,
    });
};

const persistGroupOrder = async () => {
    const groupOrderPayload = {};
    groupedChapters.value.forEach((group, index) => {
        groupOrderPayload[group.id] = index + 1;
    });

    await axios.put(route('api.course.backoffice.chapter-group.order', {
        course_id: props.courseId,
    }), {
        order: groupOrderPayload,
    });
};

const handleChapterDrop = async (event) => {
    drag.value = false;

    if (event.oldIndex === event.newIndex && event.from === event.to) {
        return;
    }

    if (savingLayout.value) {
        return;
    }

    savingLayout.value = true;
    try {
        await persistChapterLayout();
    } finally {
        savingLayout.value = false;
    }
};

const handleGroupDrop = async (event) => {
    drag.value = false;

    if (event.oldIndex === event.newIndex) {
        return;
    }

    if (savingLayout.value) {
        return;
    }

    savingLayout.value = true;
    try {
        await persistGroupOrder();
        await persistChapterLayout();
    } finally {
        savingLayout.value = false;
    }
};
</script>

<template>
    <div class="space-y-4">
        <component :is="groupedChapters.length === 0 ? 'div' : 'card-sub'">
            <h4 v-if="groupedChapters.length > 0" class="text-sm uppercase tracking-wide text-gray-500 mb-2">
                {{ _mft('course:chapter.group.ungrouped') }}
            </h4>
            <draggable
                :list="ungroupedChapters"
                item-key="id"
                handle=".chapter-drag-handle"
                :group="{ name: 'course-chapters' }"
                class="divide-y"
                ghost-class="chapter-drop-indicator"
                :animation="200"
                @start="drag = true"
                @end="handleChapterDrop"
            >
                <template #item="{ element: chapter }">
                    <div class="flex justify-between space-x-4 py-2 transition-all duration-150">
                        <div class="flex items-start space-x-2" :class="chapter.published ? 'opacity-100' : 'opacity-50'">
                            <button
                                type="button"
                                class="chapter-drag-handle text-gray-400 cursor-move px-1"
                                :aria-label="_mft('course:chapter.moveAria')"
                            >
                                <i class="fa-regular fa-grip-dots-vertical" />
                            </button>
                            <div>
                                <div>{{ chapter.title }}</div>
                                <div class="text-xs text-gray-600 whitespace-pre">{{ chapter.description }}</div>
                                <div v-if="!chapter.published" class="text-xs text-gray-400 italic">
                                    {{ _mft('course:chapter.status.draft') }}
                                </div>
                            </div>
                        </div>
                        <div class="flex items-end space-x-1">
                            <button-more-options
                                size="sm"
                                :cancel-on-outside-click="false"
                                alignment="right"
                                style-type="transparent"
                            >
                                <a
                                    :href="route('course.backoffice.chapter.preview', [courseId, chapter.id])"
                                    target="_blank"
                                    class="mf-link text-xs"
                                >
                                    <button-more-options-option size="sm" class="whitespace-nowrap">
                                        {{ _mft('shared:previewInNewTab') }}
                                    </button-more-options-option>
                                </a>

                                <button-more-options-option-with-confirmation
                                    style-type="danger"
                                    :confirm-text="_mft('shared:confirm.areYouSure')"
                                    :confirm-button-label="_mft('shared:action.delete')"
                                    :abort-button-label="_mft('shared:action.cancel')"
                                    @confirmed="deleteChapter(chapter)"
                                >
                                    <span class="text-xs">{{ _mft('course:chapter.delete') }}</span>
                                </button-more-options-option-with-confirmation>
                            </button-more-options>

                            <button-secondary
                                size="sm"
                                @click="emit('edit-settings', chapter)"
                                style-type="transparent"
                            >
                                <i class="fa-regular fa-cog" />
                            </button-secondary>

                            <a :href="route('manageCourses.courses.chapters.pageBuilder', [courseId, chapter.id])">
                                <button-secondary size="sm">
                                    <i class="fa-regular fa-pencil" />
                                </button-secondary>
                            </a>
                        </div>
                    </div>
                </template>
            </draggable>
        </component>

        <draggable
            :list="groupedChapters"
            item-key="id"
            handle=".group-drag-handle"
            class="space-y-4"
            ghost-class="group-drop-indicator"
            :animation="200"
            @start="drag = true"
            @end="handleGroupDrop"
        >
            <template #item="{ element: group }">
                <card-sub>
                        <div class="flex items-start justify-between mb-2 border-b pb-2 gap-2">
                            <div class="flex items-center space-x-2 w-full">
                                <button
                                    type="button"
                                    class="group-drag-handle text-gray-400 cursor-move px-1"
                                    :aria-label="_mft('course:chapter.group.moveAria')"
                                >
                                    <i class="fa-regular fa-grip-dots-vertical" />
                                </button>
                                <input-text
                                    v-if="editingGroupId === group.id"
                                    v-model:content="group.title"
                                    type="text"
                                    class="w-full !mb-0 max-w-lg"
                                    @blur="saveGroup(group)"
                                />
                                <h4 v-else class="text-sm tracking-wide text-gray-500">
                                    {{ group.title }}
                                </h4>
                                <button-secondary
                                    size="sm"
                                    style-type="transparent"
                                    @click="toggleGroupEdit(group.id)"
                                >
                                    <i class="fa-regular" :class="editingGroupId === group.id ? 'fa-check' : 'fa-pen text-gray-400'" />
                                </button-secondary>
                                <button-with-confirmation
                                    v-if="editingGroupId === group.id"
                                    size="sm"
                                    style-type="transparent"
                                    confirm-type="danger"
                                    :confirm-text="_mft('course:chapter.group.confirmDelete')"
                                    @confirmed="deleteGroup(group)"
                                >
                                    <i class="fa-regular fa-trash text-myflowRed" />
                                </button-with-confirmation>
                            </div>
                            <div class="flex items-center space-x-2">
                                <span class="text-xs text-gray-400 whitespace-nowrap">
                                    {{ _mft('course:chapter.group.chapterCount', {count: group.chapters.length}) }}
                                </span>
                            </div>
                        </div>

                        <draggable
                            :list="group.chapters"
                            item-key="id"
                            handle=".chapter-drag-handle"
                            :group="{ name: 'course-chapters' }"
                            class="divide-y"
                            ghost-class="chapter-drop-indicator"
                            :animation="200"
                            @start="drag = true"
                            @end="handleChapterDrop"
                        >
                            <template #item="{ element: chapter }">
                                <div class="flex justify-between space-x-4 py-2 transition-all duration-150">
                                    <div class="flex items-start space-x-2" :class="chapter.published ? 'opacity-100' : 'opacity-50'">
                                        <button
                                            type="button"
                                            class="chapter-drag-handle text-gray-400 cursor-move px-1"
                                            :aria-label="_mft('course:chapter.moveAria')"
                                        >
                                            <i class="fa-regular fa-grip-dots-vertical" />
                                        </button>
                                        <div>
                                            <div>{{ chapter.title }}</div>
                                            <div class="text-xs text-gray-600 whitespace-pre">{{ chapter.description }}</div>
                                            <div v-if="!chapter.published" class="text-xs text-gray-400 italic">
                                                {{ _mft('course:chapter.status.draft') }}
                                            </div>
                                        </div>
                                    </div>
                                    <div class="flex items-end space-x-1">
                                        <button-more-options
                                            size="sm"
                                            :cancel-on-outside-click="false"
                                            alignment="right"
                                            style-type="transparent"
                                        >
                                            <a
                                                :href="route('course.backoffice.chapter.preview', [courseId, chapter.id])"
                                                target="_blank"
                                                class="mf-link text-xs"
                                            >
                                                <button-more-options-option size="sm" class="whitespace-nowrap">
                                                    {{ _mft('shared:previewInNewTab') }}
                                                </button-more-options-option>
                                            </a>

                                            <button-more-options-option-with-confirmation
                                                style-type="danger"
                                                :confirm-text="_mft('shared:confirm.areYouSure')"
                                                :confirm-button-label="_mft('shared:action.delete')"
                                                :abort-button-label="_mft('shared:action.cancel')"
                                                @confirmed="deleteChapter(chapter)"
                                            >
                                                <span class="text-xs">{{ _mft('course:chapter.delete') }}</span>
                                            </button-more-options-option-with-confirmation>
                                        </button-more-options>

                                        <button-secondary
                                            size="sm"
                                            @click="emit('edit-settings', chapter)"
                                            style-type="transparent"
                                        >
                                            <i class="fa-regular fa-cog" />
                                        </button-secondary>

                                        <a :href="route('manageCourses.courses.chapters.pageBuilder', [courseId, chapter.id])">
                                            <button-secondary size="sm" style-type="transparent">
                                                <i class="fa-regular fa-pencil" />
                                            </button-secondary>
                                        </a>
                                    </div>
                                </div>
                            </template>
                        </draggable>
                </card-sub>
            </template>
        </draggable>
    </div>

    <ul class="-mx-4 md:-mx-6 lg:-mx-8 mt-4">
        <li class="flex flex-col md:flex-row md:items-center md:justify-between gap-2 py-2 px-4 md:px-6 lg:px-8">
            <div class="flex flex-col md:flex-row md:items-center gap-2 w-full md:max-w-lg">
                <button-secondary
                    @click="toggleCreateGroupForm"
                    class="whitespace-nowrap"
                >
                    {{ showCreateGroupForm ? _mft('shared:action.cancel') : _mft('course:chapter.group.add') }}
                </button-secondary>

                <div v-if="showCreateGroupForm" class="flex items-center space-x-2 w-full">
                    <input-text
                        v-model:content="newGroupTitle"
                        type="text"
                        class="w-full !mb-0"
                        :placeholder="_mft('course:chapter.group.newTitlePlaceholder')"
                    />
                    <button-secondary
                        @click="createGroup"
                        :disabled="creatingGroup || newGroupTitle.trim().length === 0"
                        class="whitespace-nowrap"
                    >
                        {{ _mft('shared:action.add') }}
                    </button-secondary>
                </div>
            </div>

            <a :href="route('course.backoffice.chapter.add', courseId)">
                <button-primary>
                    {{ _mft('course:addChapter') }}
                </button-primary>
            </a>
        </li>
    </ul>
</template>

<style scoped>
.chapter-drop-indicator {
    --chapter-drop-line-thickness: 4px;
    --chapter-drop-line-gap: 0.5rem;
    position: relative;
    display: block !important;
    opacity: 1 !important;
    height: calc(var(--chapter-drop-line-thickness) + (var(--chapter-drop-line-gap) * 2)) !important;
    min-height: calc(var(--chapter-drop-line-thickness) + (var(--chapter-drop-line-gap) * 2)) !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    overflow: visible !important;
}

.chapter-drop-indicator > * {
    display: none !important;
}

.chapter-drop-indicator::before {
    content: '';
    position: absolute;
    left: 1rem;
    right: 1rem;
    top: var(--chapter-drop-line-gap);
    height: var(--chapter-drop-line-thickness);
    background: var(--whitelabel-primary);
    border-radius: 9999px;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.95);
    z-index: 1;
}

.group-drop-indicator {
    opacity: 0.5;
}
</style>
